/**
 * EnemyManager - Manages all enemies on the client
 */
import Enemy from '../core/Enemy.js';

export class EnemyManager {
  /**
   * Enemy Manager constructor
   * @param {Object} options - Configuration options
   * @param {Object} options.socket - Socket.io client instance
   * @param {Object} options.renderer - Enemy renderer instance
   * @param {Object} options.projectileManager - Projectile manager for ranged attacks
   */
  constructor(options = {}) {
    console.log('Initializing EnemyManager with options:', {
      hasSocket: !!options.socket,
      hasRenderer: !!options.renderer,
      hasProjectileManager: !!options.projectileManager
    });
    
    if (!options.socket) {
      console.warn('EnemyManager initialized without a socket connection');
    }
    
    if (!options.renderer) {
      console.error('EnemyManager initialized without a renderer - enemies will not be visible');
    }
    
    this.socket = options.socket;
    this.renderer = options.renderer;
    this.projectileManager = options.projectileManager;
    this.enemies = new Map();
    
    // Initialize with an empty server data store
    this.serverEnemyData = {};
    
    // Keep track of the last time animations were updated
    this.lastAnimationUpdateTime = Date.now();
    
    // Attack cooldown tracking
    this.enemyAttackCooldowns = new Map();
    
    console.log('EnemyManager initialized successfully');
  }

  /**
   * Create an enemy from server data and add it to the renderer
   * @param {Object} data - Enemy data from server
   * @returns {Enemy|null} The created enemy or null if creation failed
   * @note This method both creates the enemy AND adds it to the renderer,
   * so there's no need to call renderer.addEnemy() separately.
   */
  createEnemy(data) {
    try {
      if (!data || !data.id) {
        console.error('Invalid enemy data received:', data);
        return null;
      }
      
      // Check if it's a boss enemy
      const isBoss = data.isBoss === true;
      const scale = data.scale || (isBoss ? 3.0 : 1.0);
      
      if (isBoss) {
        console.log(`👹 Creating BOSS enemy ${data.id} at position:`, data.position);
      } else {
        console.log(`Creating enemy ${data.id} at position:`, data.position);
      }
      
      console.log(`Enemy data:`, {
        type: data.type,
        health: data.health,
        speed: data.speed,
        position: data.position,
        isBoss: isBoss,
        scale: scale
      });
      
      // Determine the proper enemy type based on provided type or default
      let enemyType = data.type || 'default';
      
      // Map generic types to specific skeleton types if needed
      if (enemyType === 'default') enemyType = 'melee';
      
      // Create an enemy instance
      const enemy = new Enemy({
        id: data.id,
        position: data.position,
        type: enemyType,
        health: data.health || 100,
        maxHealth: data.maxHealth || data.health || 100,
        level: data.level || 1,
        speed: data.speed, // Pass the server-provided speed
        isBoss: isBoss,
        scale: scale
      });
      
      // Store the enemy by ID
      this.enemies.set(data.id, enemy);
      
      // Add the enemy to the renderer if available
      if (this.renderer) {
        this.renderer.addEnemy(enemy);
        
        // Force update the scale and boss status if needed
        if (isBoss) {
          setTimeout(() => {
            const obj = this.renderer.getEnemyObject(data.id);
            if (obj && obj.mesh) {
              obj.mesh.scale.set(scale, scale, scale);
              console.log(`👹 Verified boss enemy scale is set to ${scale}x`);
            }
          }, 100);
        }
      } else {
        console.warn(`Cannot render enemy ${data.id} - renderer not available`);
      }
      
      // CRITICAL FIX: Ensure new enemies start in an active state with correct initial data
      enemy.isActive = true;
      enemy.lastUpdateTime = Date.now();
      
      // Store this creation time to detect stuck enemies later
      enemy.creationTime = Date.now();
      
      return enemy;
    } catch (error) {
      console.error('Error creating enemy:', error);
      return null;
    }
  }
  
  /**
   * Update enemy positions and state
   * @param {Object} players - Object containing all players (local and remote)
   * @param {number} deltaTime - Time since last update in seconds
   */
  update(players, deltaTime = 1/60) {
    if (!players || Object.keys(players).length === 0) {
      // No players to update against
      return;
    }
    
    try {
      // Process any pending server updates
      this._processServerUpdates();
      
      // CRITICAL FIX: Check for stuck enemies
      this._checkForStuckEnemies(players);
      
      // Update each enemy
      this.enemies.forEach(enemy => {
        if (enemy && typeof enemy.update === 'function') {
          // Pass the projectile manager to the enemy update method for ranged attacks
          enemy.update(players, this.projectileManager);
          
          // Update enemy visual representation with current animation state
          if (this.renderer) {
            this.renderer.updateEnemy(
              enemy, 
              enemy.position, 
              enemy.health, 
              enemy.maxHealth, 
              enemy.currentAnimation
            );
          }
        }
      });
      
      // Update attack cooldowns
      const now = Date.now();
      this.enemyAttackCooldowns.forEach((cooldownTime, enemyId) => {
        if (now >= cooldownTime) {
          this.enemyAttackCooldowns.delete(enemyId);
        }
      });
    } catch (error) {
      console.error('Error updating enemies:', error);
    }
  }
  
  /**
   * Check for and attempt to fix stuck enemies
   * @param {Object} players - Available players
   * @private
   */
  _checkForStuckEnemies(players) {
    const now = Date.now();
    const activePlayerCount = Object.values(players).filter(p => p && p.position).length;
    
    // Skip checks if we don't have any players
    if (activePlayerCount === 0) return;
    
    // Check every 3 seconds to avoid performance impact
    if (now % 3000 > 100) return;
    
    console.log(`Checking for stuck enemies among ${this.enemies.size} total enemies`);
    let stuckCount = 0;
    
    this.enemies.forEach((enemy, id) => {
      // Skip recent enemies
      const enemyAge = now - (enemy.creationTime || 0);
      if (enemyAge < 5000) return;
      
      // Check for signs of being stuck
      const lastUpdateAge = now - enemy.lastUpdateTime;
      const hasTarget = !!enemy.targetPlayer;
      const hasServerPosition = !!enemy.serverPosition;
      
      // Detect stuck enemies - those without targets or not updating
      if (!hasTarget || lastUpdateAge > 3000 || !hasServerPosition) {
        stuckCount++;
        console.log(`Potentially stuck enemy detected: ${id}`);
        console.log(`  Has target: ${hasTarget}, Last update: ${lastUpdateAge}ms ago, Has server pos: ${hasServerPosition}`);
        
        // Reset enemy state to make it responsive again
        enemy.state = 'idle';
        enemy.targetPlayer = null;
        
        // Force it to try finding a target immediately
        const anyPlayer = Object.values(players).find(p => p && p.position);
        if (anyPlayer) {
          console.log(`  Assigning emergency target: ${anyPlayer.id}`);
          // Clone the player data to avoid sharing references
          enemy.targetPlayer = {
            id: anyPlayer.id,
            position: { ...anyPlayer.position }
          };
        }
        
        // Reset update time
        enemy.lastUpdateTime = now;
        enemy.isActive = true;
      }
    });
    
    if (stuckCount > 0) {
      console.log(`Found and attempted to fix ${stuckCount} stuck enemies`);
    }
  }
  
  /**
   * Process pending server updates
   * @private
   */
  _processServerUpdates() {
    // Check if we have any server data to process
    if (!this.serverEnemyData || Object.keys(this.serverEnemyData).length === 0) {
      return;
    }
    
    // Get current time
    const now = Date.now();
    
    // Process each enemy update
    for (const id in this.serverEnemyData) {
      const serverData = this.serverEnemyData[id];
      
      // Detailed logging for the first few enemies each session
      const shouldLog = this.loggingCounter < 5;
      if (shouldLog) {
        this.loggingCounter++;
      }
      
      // Get the enemy instance if it exists
      const enemy = this.enemies.get(id);
      
      if (enemy) {
        const isNewEnemy = now - enemy.createdTime < 3000;
        
        // Update position from server if provided
        if (serverData.position) {
          // Store the server position for smooth interpolation
          enemy.serverPosition = { ...serverData.position };
          
          // If this is the first position update, set immediate position
          if (!enemy.hasReceivedPosition || isNewEnemy) {
            if (shouldLog) {
              console.log(`Setting initial position for enemy ${id}`);
            }
            enemy.position = { ...serverData.position };
            enemy.hasReceivedPosition = true;
          }
          
          // Update last server position time
          enemy.lastServerPositionUpdate = now;
        }
        
        // Update health if it exists
        if (serverData.health !== undefined && serverData.health !== enemy.health) {
          // Log when health changes
          if (Math.abs(serverData.health - enemy.health) > 0.1) {
            console.log(`Enemy ${id} health updated: ${enemy.health} -> ${serverData.health}`);
          }
          
          // Update the enemy's health
          enemy.health = serverData.health;
          
          // Prepare extra properties for the renderer
          const extras = {};
          
          // Pass boss status if provided
          if (serverData.isBoss !== undefined) {
            extras.isBoss = serverData.isBoss;
            enemy.isBoss = serverData.isBoss;
          }
          
          // Pass scale if provided
          if (serverData.scale !== undefined) {
            extras.scale = serverData.scale;
            enemy.scale = serverData.scale;
          }
          
          // Update the health bar if the renderer is available
          if (this.renderer) {
            this.renderer.updateEnemy(
              enemy, 
              null, 
              serverData.health, 
              serverData.maxHealth || enemy.maxHealth,
              null,
              extras
            );
          }
          
          // Check if the enemy should be destroyed
          if (serverData.health <= 0) {
            console.log(`Enemy ${id} health is zero or below, marking for removal`);
            // We don't remove here - the server will send a destroy event
          }
        }
        
        // Check for specific animation states from server
        else if (serverData.animationState) {
          // Don't override attack animations that are in progress
          const inAttackAnimation = enemy.currentAnimation === 'attack' && 
                                   (now - enemy.lastAttackTime < 600);
          
          if (!inAttackAnimation) {
            // Update animation state from server
            enemy.currentAnimation = serverData.animationState;
            
            if (shouldLog) {
              console.log(`Enemy ${id} animation updated to ${serverData.animationState} from server`);
            }
          } else if (shouldLog) {
            console.log(`Skipped animation update for enemy ${id} - attack in progress`);
          }
        }
        
        // CRITICAL FIX: If server provides target player, update enemy's target directly
        if (serverData.targetPlayer) {
          // Check if the target player exists in our world
          const targetPlayerId = serverData.targetPlayer;
          
          if (shouldLog) {
            console.log(`Enemy ${id} server target player: ${targetPlayerId}`);
          }
        }
        
        // Update any other properties from the server
        if (serverData.maxHealth !== undefined && serverData.maxHealth !== enemy.maxHealth) {
          enemy.maxHealth = serverData.maxHealth;
        }
        
        if (serverData.level !== undefined && serverData.level !== enemy.level) {
          enemy.level = serverData.level;
        }
        
        // Handle boss status and scaling if provided
        if (serverData.isBoss !== undefined || serverData.scale !== undefined) {
          const extras = {};
          
          if (serverData.isBoss !== undefined) {
            extras.isBoss = serverData.isBoss;
            enemy.isBoss = serverData.isBoss;
            
            if (shouldLog && serverData.isBoss) {
              console.log(`Enemy ${id} marked as BOSS`);
            }
          }
          
          if (serverData.scale !== undefined) {
            extras.scale = serverData.scale;
            enemy.scale = serverData.scale;
            
            if (shouldLog) {
              console.log(`Enemy ${id} scale set to ${serverData.scale}x`);
            }
          }
          
          // Update the renderer if we have extra properties
          if (Object.keys(extras).length > 0 && this.renderer) {
            this.renderer.updateEnemy(
              enemy,
              null,
              enemy.health,
              enemy.maxHealth,
              null,
              extras
            );
          }
        }
        
        // Update speed if provided - use higher speed for new enemies
        if (serverData.speed !== undefined) {
          // New enemies get higher speed initially to ensure visible movement
          const speedBoost = isNewEnemy ? 1.5 : 1.0;
          const newSpeed = Math.max(serverData.speed * speedBoost, 2); // Ensure minimum speed
          
          if (enemy.speed !== newSpeed) {
            if (shouldLog) {
              console.log(`Updating speed for enemy ${id}: ${enemy.speed} → ${newSpeed}`);
            }
            enemy.speed = newSpeed;
          }
        }
        
        // Update state if provided
        if (serverData.state !== undefined) {
          if (enemy.state !== serverData.state && shouldLog) {
            console.log(`Updating state for enemy ${id}: ${enemy.state} -> ${serverData.state}`);
          }
          enemy.state = serverData.state;
        }
      } else {
        // Enemy doesn't exist yet, create it if we have enough data
        if (serverData.position) {
          const newEnemy = this.createEnemy({
            id: id,
            position: serverData.position,
            type: serverData.type || 'default',
            health: serverData.health || 100,
            maxHealth: serverData.maxHealth || 100,
            level: serverData.level || 1,
            speed: serverData.speed,
            isBoss: serverData.isBoss,
            scale: serverData.scale
          });
          
          if (newEnemy && shouldLog) {
            console.log(`Created new enemy ${id} from server data`);
            if (serverData.isBoss) {
              console.log(`☠️ New enemy ${id} is a BOSS (scale: ${serverData.scale || 'default'})`);
            }
          }
        }
      }
    }
    
    // Clear the server data after processing
    this.serverEnemyData = {};
  }
  
  /**
   * Get all enemies as a Map
   * @returns {Map<string, Object>} - Map of enemy IDs to enemy objects
   */
  getEnemies() {
    return this.enemies;
  }
  
  /**
   * Get a specific enemy by ID
   * @param {string} id - Enemy ID
   * @returns {Object|null} - The enemy object or null if not found
   */
  getEnemy(id) {
    return this.enemies.get(id) || null;
  }
  
  /**
   * Update an enemy with server data
   * @param {Object} data - Enemy update data from server
   * @returns {Object|null} - The updated enemy or null if update failed
   */
  updateEnemy(data) {
    if (!data || !data.id) {
      console.warn('Invalid enemy update data:', data);
      return null;
    }
    
    const enemyId = data.id;
    const enemy = this.enemies.get(enemyId);
    
    if (!enemy) {
      console.warn(`Enemy not found for update: ${enemyId}`);
      return null;
    }
    
    // Store the update in serverEnemyData for processing in _processServerUpdates
    const updatedData = { ...enemy, ...data };
    this.serverEnemyData[enemyId] = data;
    
    return enemy;
  }
  
  /**
   * Remove an enemy from the manager
   * @param {string|Object} enemyId - ID of enemy to remove, or object with id property
   */
  removeEnemy(enemyId) {
    // CRITICAL FIX: Handle object vs. string ID properly
    let id;
    
    if (typeof enemyId === 'object') {
      // If we were passed an object, extract the ID
      id = enemyId.id || enemyId;
      console.log(`Normalizing object to ID: ${id}`);
    } else {
      // Direct string ID
      id = enemyId;
    }
    
    // If it's still an object after normalization, it's malformed
    if (typeof id === 'object') {
      console.error(`Invalid enemy ID format: received object instead of string:`, id);
      // Try to extract ID using multiple approaches
      if (id.id) {
        id = id.id;
        console.log(`Extracted ID from object: ${id}`);
      } else {
        console.error(`Cannot extract ID from object, dumping object:`, JSON.stringify(id));
        return; // Can't proceed with removal
      }
    }
    
    console.log(`Removing enemy ${id}`);
    
    // Check if the enemy exists in our collection
    const enemyExists = this.enemies.has(id);
    if (!enemyExists) {
      console.warn(`Enemy ${id} not found for removal`);
      // Still attempt to clean up renderer and other references
    }
    
    // Get the enemy object if it exists
    const enemy = this.enemies.get(id);
    
    // Remove from renderer first (even if enemy wasn't found in our map)
    if (this.renderer) {
      try {
        this.renderer.removeEnemy(id);
      } catch (error) {
        console.error(`Error removing enemy ${id} from renderer:`, error);
      }
      
      // Additional scene cleanup for missing enemies
      if (!enemyExists && this.renderer.scene) {
        try {
          // Try to find and remove any objects with this ID in the scene directly
          const sceneObject = this.renderer.scene.getObjectByName(id);
          if (sceneObject) {
            console.log(`Found orphaned enemy object ${id} in scene, removing directly`);
            this.renderer.scene.remove(sceneObject);
          }
        } catch (error) {
          console.error(`Error cleaning up scene for enemy ${id}:`, error);
        }
      }
    }
    
    // Delete from collections if it exists
    if (enemyExists) {
      this.enemies.delete(id);
    }
    
    // Clean up other references regardless
    this.enemyAttackCooldowns.delete(id);
    if (id in this.serverEnemyData) {
      delete this.serverEnemyData[id];
    }
    
    if (enemyExists) {
      console.log(`Enemy ${id} successfully removed`);
    } else {
      console.log(`Enemy ${id} references cleaned up`);
    }
  }
  
  /**
   * Handle events from the server
   * @param {string} eventName - Name of the event
   * @param {Object} data - Event data
   */
  handleServerEvent(eventName, data) {
    try {
      // Special handling for boss enemies
      if (data && (data.isBoss === true)) {
        console.log(`🔄 Handling ${eventName} for boss enemy:`, data.id);
      }
      
      // Handle different event types
      switch (eventName) {
        case 'enemySpawn':
          // Check if it's a boss enemy
          if (data && data.isBoss === true) {
            console.log(`👹 Boss enemy spawn detected in enemySpawn event: ${data.id}`);
            // Create a new boss enemy with proper boss attributes
            const enemy = this.createEnemy({
              ...data,
              isBoss: true,
              scale: data.scale || 3.0
            });
            
            // Create boss effects
            if (enemy) {
              this._createBossEffect(enemy);
            }
          } else {
            // Create a regular enemy
            this.createEnemy(data);
          }
          break;
          
        case 'enemyUpdate':
          // Check if this update is for a boss
          if (data && data.isBoss === true) {
            console.log(`🔄 Boss enemy update: ${data.id}`);
            
            // Get the existing enemy
            const existingEnemy = this.enemies.get(data.id);
            
            if (existingEnemy) {
              // Update boss status
              existingEnemy.isBoss = true;
              existingEnemy.scale = data.scale || 3.0;
              
              // Store the update with boss properties explicitly set
              this.serverEnemyData[data.id] = {
                ...data,
                isBoss: true,
                scale: data.scale || 3.0
              };
              
              // Force immediate update to renderer
              if (this.renderer) {
                this.renderer.updateEnemy(
                  existingEnemy,
                  data.position,
                  data.health,
                  data.maxHealth,
                  existingEnemy.currentAnimation,
                  {
                    isBoss: true,
                    scale: data.scale || 3.0
                  }
                );
              }
            } else {
              // Boss enemy doesn't exist yet, create it
              console.log(`👹 Creating boss enemy from update event: ${data.id}`);
              if (data.position) {
                const newBoss = this.createEnemy({
                  ...data,
                  isBoss: true,
                  scale: data.scale || 3.0
                });
                
                if (newBoss) {
                  this._createBossEffect(newBoss);
                }
              } else {
                console.warn(`👹 Cannot create boss enemy without position data:`, data);
                // Store data for later processing
                this.serverEnemyData[data.id] = {
                  ...data,
                  isBoss: true,
                  scale: data.scale || 3.0
                };
              }
            }
          } else {
            // Regular enemy update
            this.updateEnemy(data);
          }
          break;
          
        case 'enemyDestroy':
          // CRITICAL FIX: Properly handle both string and object formats
          let enemyId;
          
          if (typeof data === 'string') {
            // Direct string ID
            enemyId = data;
          } else if (data && typeof data === 'object') {
            // Object with ID property
            enemyId = data.id;
          } else {
            console.error('Invalid enemyDestroy data format:', data);
            return;
          }
          
          if (!enemyId) {
            console.error('Missing enemy ID in destroy event:', data);
            return;
          }
          
          // Check if this was a boss enemy
          const enemy = this.enemies.get(enemyId);
          if (enemy && enemy.isBoss) {
            console.log(`👹 Boss enemy destroyed: ${enemyId}`);
          }
          
          // Remove the enemy
          this.removeEnemy(enemyId);
          break;
        
        case 'enemy:destroy':
          // CRITICAL FIX: Properly handle typed event format
          if (!data || !data.id) {
            console.error('Invalid enemy:destroy data format:', data);
            return;
          }
          
          // Remove the enemy using the ID string, not the whole object
          this.removeEnemy(data.id);
          break;
      }
    } catch (error) {
      console.error(`Error handling server event ${eventName}:`, error);
    }
  }
  
  /**
   * Create visual effects for boss enemy
   * @param {Object} bossEnemy - The boss enemy object
   * @private
   */
  _createBossEffect(bossEnemy) {
    if (!this.renderer || !this.renderer.scene) {
      console.warn('Cannot create boss effects: renderer or scene not available');
      return;
    }
    
    // Simple effect: add a glow to the boss
    try {
      const THREE = window.THREE;
      if (!THREE) return;
      
      // Get boss position
      const position = bossEnemy.position || { x: 0, y: 0, z: 0 };
      
      // Add a point light
      const bossLight = new THREE.PointLight(0xff0000, 2, 10);
      bossLight.position.set(position.x, position.y + 2, position.z);
      this.renderer.scene.add(bossLight);
      
      // Store light in boss data for updates
      bossEnemy.light = bossLight;
      
      // Pulse effect
      let intensity = 2;
      let increasing = false;
      
      // Light animation
      const animateLight = () => {
        if (!bossEnemy || !this.enemies.has(bossEnemy.id)) {
          // Boss is gone, remove light
          if (bossLight && bossLight.parent) {
            bossLight.parent.remove(bossLight);
          }
          return;
        }
        
        // Update light position to follow boss
        if (bossLight && bossEnemy.position) {
          bossLight.position.set(
            bossEnemy.position.x,
            bossEnemy.position.y + 2,
            bossEnemy.position.z
          );
          
          // Pulse effect
          if (increasing) {
            intensity += 0.05;
            if (intensity >= 3) {
              increasing = false;
            }
          } else {
            intensity -= 0.05;
            if (intensity <= 1) {
              increasing = true;
            }
          }
          
          bossLight.intensity = intensity;
        }
        
        // Continue animation
        requestAnimationFrame(animateLight);
      };
      
      // Start animation
      requestAnimationFrame(animateLight);
      
      // Add floating text label for boss (for testing)
      console.log('👹 Creating boss label effect');
      
    } catch (error) {
      console.error('Error creating boss effects:', error);
    }
  }

  /**
   * Fix any missing boss enemy properties or visuals
   * This helps with mid-match joining issues
   * @param {string} socketId - The socket ID (optional)
   */
  fixBossEnemyVisuals() {
    // Go through all enemies and check if any bosses need visual fixes
    let fixedCount = 0;
    
    this.enemies.forEach((enemy, id) => {
      if (enemy.isBoss) {
        console.log(`👹 Checking boss enemy ${id} for visual fixes`);
        
        // Check if the renderer has this enemy object
        if (this.renderer) {
          const enemyObj = this.renderer.getEnemyObject(id);
          
          if (enemyObj && enemyObj.mesh) {
            // Check if scale is properly applied
            const scale = enemy.scale || 3.0;
            const currentScale = enemyObj.mesh.scale.x;
            
            if (Math.abs(currentScale - scale) > 0.1) {
              console.log(`👹 Fixing scale for boss ${id}: ${currentScale} → ${scale}`);
              enemyObj.mesh.scale.set(scale, scale, scale);
              fixedCount++;
            }
            
            // Check if boss has light effect
            if (!enemy.light) {
              console.log(`👹 Adding missing light effect to boss ${id}`);
              this._createBossEffect(enemy);
              fixedCount++;
            }
          } else {
            console.log(`👹 Boss ${id} missing from renderer, requesting recreate`);
            
            // Force recreate the boss enemy visual
            setTimeout(() => {
              if (this.enemies.has(id) && this.renderer) {
                console.log(`👹 Recreating boss ${id} visual`);
                this.renderer.removeEnemy(id);
                this.renderer.addEnemy(enemy);
                
                // Reapply scale after a brief delay
                setTimeout(() => {
                  const obj = this.renderer.getEnemyObject(id);
                  if (obj && obj.mesh) {
                    const scale = enemy.scale || 3.0;
                    obj.mesh.scale.set(scale, scale, scale);
                    console.log(`👹 Reapplied scale ${scale}x to boss ${id}`);
                  }
                }, 100);
              }
            }, 500);
            
            fixedCount++;
          }
        }
      }
    });
    
    return fixedCount;
  }

  /**
   * Register socket event handlers
   */
  registerSocketHandlers() {
    if (!this.socket) {
      console.warn('Cannot register socket handlers: no socket available');
      return;
    }
    
    try {
      // Register for enemy spawn events
      this.socket.on('enemySpawn', (data) => {
        console.log('Received enemySpawn event:', data);
        this.handleServerEvent('enemySpawn', data);
      });
      
      // Register for enemy update events
      this.socket.on('enemyUpdate', (data) => {
        this.handleServerEvent('enemyUpdate', data);
      });

      // Register for enemy:update events (alternative format)
      this.socket.on('enemy:update', (data) => {
        this.handleServerEvent('enemyUpdate', data);
      });
      
      // Register for enemy destroy events
      this.socket.on('enemyDestroy', (data) => {
        console.log('Received enemyDestroy event:', data);
        // Check if data is just an ID string or an object with id property
        const enemyId = typeof data === 'string' ? data : data.id;
        this.handleServerEvent('enemyDestroy', { id: enemyId });
      });
      
      // EMERGENCY FIX: Add handler for force destroy events
      this.socket.on('forceDestroyEnemy', (data) => {
        console.log('⚠️ FORCE DESTROY ENEMY received:', data);
        try {
          const enemyId = data.id;
          if (enemyId) {
            console.log(`🛠️ Force removing enemy ${enemyId} from client`);
            // Use our fixed removeEnemy method
            this.removeEnemy(enemyId);
            
            // Even if not found in our collections, try to clean up any mesh directly from scene
            if (this.renderer && this.renderer.scene) {
              // Try both the ID as string and as object with name property
              let enemyObject = this.renderer.scene.getObjectByName(enemyId);
              
              if (!enemyObject && typeof enemyId === 'object' && enemyId.id) {
                enemyObject = this.renderer.scene.getObjectByName(enemyId.id);
              }
              
              if (enemyObject) {
                console.log(`🛠️ Found enemy object in scene directly, removing...`);
                this.renderer.scene.remove(enemyObject);
              }
            }
          } else {
            console.error('⚠️ Invalid enemyId in forceDestroyEnemy event:', data);
          }
        } catch (err) {
          console.error('Error handling forceDestroyEnemy:', err);
        }
      });
      
      // Register for enemy attack events
      this.socket.on('enemyAttack', (data) => {
        this.handleServerEvent('enemyAttack', data);
      });
      
      // Register for boss spawn events
      this.socket.on('bossSpawn', (data) => {
        console.log('👹 Received bossSpawn event:', data);
        
        // Create or update the boss enemy with priority
        if (data && data.id) {
          // Delay slightly to ensure the game is ready
          setTimeout(() => {
            // Check if we already have this enemy
            const existingEnemy = this.enemies.get(data.id);
            
            if (existingEnemy) {
              console.log(`👹 Updating existing boss enemy ${data.id}`);
              // Make sure the boss properties are set
              existingEnemy.isBoss = true;
              existingEnemy.scale = data.scale || 3.0;
              
              // Force update in the renderer
              if (this.renderer) {
                this.renderer.updateEnemy(
                  existingEnemy,
                  data.position,
                  data.health,
                  data.maxHealth,
                  null,
                  {
                    isBoss: true,
                    scale: data.scale || 3.0
                  }
                );
                
                // Force update the mesh scale directly
                const enemyObj = this.renderer.getEnemyObject(data.id);
                if (enemyObj && enemyObj.mesh) {
                  const scale = data.scale || 3.0;
                  enemyObj.mesh.scale.set(scale, scale, scale);
                  console.log(`👹 Force updated boss mesh scale to ${scale}x`);
                } else {
                  console.log(`👹 Failed to find mesh for boss ${data.id}, will try again in 500ms`);
                  
                  // Retry after a delay (mesh might be loading)
                  setTimeout(() => this.fixBossEnemyVisuals(), 500);
                }
              }
            } else {
              console.log(`👹 Creating new boss enemy ${data.id}`);
              // Create a new boss enemy with all required properties
              const bossEnemy = this.createEnemy({
                ...data,
                isBoss: true,
                scale: data.scale || 3.0
              });
              
              if (bossEnemy) {
                console.log(`👹 Successfully created boss enemy ${data.id}`);
                
                // Create visual effects for the boss after a short delay to ensure model is loaded
                setTimeout(() => {
                  if (this.enemies.has(data.id)) {
                    this._createBossEffect(bossEnemy);
                    
                    // Double-check the scale is applied correctly
                    const enemyObj = this.renderer?.getEnemyObject(data.id);
                    if (enemyObj && enemyObj.mesh) {
                      const scale = data.scale || 3.0;
                      enemyObj.mesh.scale.set(scale, scale, scale);
                    }
                  }
                }, 300);
              } else {
                console.error(`👹 Failed to create boss enemy ${data.id}`);
                
                // Store the data for later processing
                this.serverEnemyData[data.id] = {
                  ...data,
                  isBoss: true,
                  scale: data.scale || 3.0
                };
              }
            }
          }, 200); // Add a small delay to ensure the game is ready
        }
      });
      
      // Connected event - fix any boss enemies that might be missing when player connects
      this.socket.on('connect', () => {
        console.log('🔄 Socket connected, fixing any boss enemies in 2 seconds...');
        setTimeout(() => this.fixBossEnemyVisuals(), 2000);
      });

      console.log('EnemyManager socket handlers registered');
    } catch (error) {
      console.error('Error registering socket handlers:', error);
    }
  }

  /**
   * Clear all enemies
   */
  clear() {
    // Remove each enemy properly first
    this.enemies.forEach((enemy, id) => {
      this.removeEnemy(id);
    });
    
    // Clear all collections
    this.enemies.clear();
    this.enemyAttackCooldowns.clear();
    this.serverEnemyData = {};
  }
  
  /**
   * Dispose of all resources
   */
  dispose() {
    this.clear();
    
    // Unregister socket handlers if socket exists
    if (this.socket) {
      this.socket.off('enemySpawn');
      this.socket.off('enemyUpdate');
      this.socket.off('enemyDestroy');
      this.socket.off('enemyAttack');
    }
  }
} 